import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Flex,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatGroup,
  SimpleGrid,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Progress,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FiChevronRight } from 'react-icons/fi';
import { fetchTemplateStats } from '../../../../api/supabase/automations';
import { transformTemplateToCamelCase } from '../../../../api/supabase/automations';
import { useAuth } from '../../../../contexts/AuthContext';
import { isPersonalTemplate, isCompanyAutomation } from '../../../../utils/campaignAccess';

const CATEGORY_LABELS = {
  'lead-conversion': 'Lead Conversion',
  'in-process': 'In-Process',
  'post-close': 'Post-Close',
  'partner': 'Partner Management',
  'custom': 'Custom',
};

const CATEGORY_COLORS = {
  'lead-conversion': 'blue',
  'in-process': 'green',
  'post-close': 'purple',
  'partner': 'orange',
  'custom': 'gray',
};

export default function CampaignAnalytics() {
  const { currentUser, SuperAdmin } = useAuth();
  const isAdmin = SuperAdmin || currentUser?.CRMRole === 'CRM Admin';
  const [allStats, setAllStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const bgPage = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.700');

  useEffect(() => {
    (async () => {
      try {
        const raw = await fetchTemplateStats();
        setAllStats(raw.map(transformTemplateToCamelCase));
      } catch (err) {
        console.error('Failed to load analytics:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Privacy: other users' personal campaigns never appear in rows or totals —
  // only company campaigns and the viewer's own personal ones. Company
  // Automations (admin-only backend campaigns) are hidden from non-admins.
  const stats = useMemo(
    () => allStats
      .filter((s) => !isPersonalTemplate(s) || s.createdBy === currentUser?.uid)
      .filter((s) => isAdmin || !isCompanyAutomation(s)),
    [allStats, currentUser?.uid, isAdmin]
  );

  const totals = useMemo(() => {
    return stats.reduce((acc, s) => ({
      subscribers: acc.subscribers + (s.totalSubscribers || 0),
      active: acc.active + (s.activeSubscribers || 0),
      completed: acc.completed + (s.completedQueueItems || 0),
      failed: acc.failed + (s.failedQueueItems || 0),
      pending: acc.pending + (s.pendingQueueItems || 0),
      funded: acc.funded + (s.fundedDeals || 0),
      volume: acc.volume + (s.fundedVolume || 0),
    }), { subscribers: 0, active: 0, completed: 0, failed: 0, pending: 0, funded: 0, volume: 0 });
  }, [stats]);

  if (loading) {
    return <Flex justify="center" align="center" minH="60vh"><Spinner size="xl" /></Flex>;
  }

  return (
    <Box bg={bgPage} minH="100vh" pb={10}>
      <Container maxW="container.xl" pt={6}>
        <Breadcrumb spacing="8px" separator={<FiChevronRight color="gray.500" />} mb={4}>
          <BreadcrumbItem>
            <BreadcrumbLink as={RouterLink} to="/crm/campaigns">Campaigns</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink>Analytics</BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>

        <Heading size="lg" mb={6}>Campaign Analytics</Heading>

        {/* Overview Stats */}
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={8} data-tour="campstats-cards">
          <Box bg={cardBg} p={5} borderRadius="xl" shadow="sm">
            <Text fontSize="sm" color="gray.500">Total Campaigns</Text>
            <Text fontSize="3xl" fontWeight="bold">{stats.length}</Text>
          </Box>
          <Box bg={cardBg} p={5} borderRadius="xl" shadow="sm">
            <Text fontSize="sm" color="gray.500">Active Subscribers</Text>
            <Text fontSize="3xl" fontWeight="bold" color="green.500">{totals.active}</Text>
          </Box>
          <Box bg={cardBg} p={5} borderRadius="xl" shadow="sm">
            <Text fontSize="sm" color="gray.500">Steps Completed</Text>
            <Text fontSize="3xl" fontWeight="bold" color="blue.500">{totals.completed.toLocaleString()}</Text>
          </Box>
          <Box bg={cardBg} p={5} borderRadius="xl" shadow="sm">
            <Text fontSize="sm" color="gray.500">Funded Volume</Text>
            <Text fontSize="3xl" fontWeight="bold" color="purple.500">
              ${(totals.volume / 1000000).toFixed(1)}M
            </Text>
          </Box>
        </SimpleGrid>

        {/* Per-campaign table */}
        <Box bg={cardBg} borderRadius="xl" shadow="sm" overflow="hidden" data-tour="campstats-table">
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Campaign</Th>
                <Th>Category</Th>
                <Th isNumeric>Subscribers</Th>
                <Th isNumeric>Completed</Th>
                <Th isNumeric>Failed</Th>
                <Th>Success Rate</Th>
                <Th isNumeric>Funded Deals</Th>
              </Tr>
            </Thead>
            <Tbody>
              {stats.map((s) => {
                const total = (s.completedQueueItems || 0) + (s.failedQueueItems || 0);
                const rate = total > 0 ? Math.round(((s.completedQueueItems || 0) / total) * 100) : 0;
                const catColor = CATEGORY_COLORS[s.category] || 'gray';

                return (
                  <Tr key={s.templateId}>
                    <Td>
                      <Text fontWeight="medium">{s.name}</Text>
                      {s.isPlaybook && <Badge fontSize="9px" colorScheme="purple" ml={1}>Playbook</Badge>}
                    </Td>
                    <Td>
                      <Badge colorScheme={catColor} fontSize="xs">
                        {CATEGORY_LABELS[s.category] || s.category}
                      </Badge>
                    </Td>
                    <Td isNumeric>{s.activeSubscribers || 0}</Td>
                    <Td isNumeric>{s.completedQueueItems || 0}</Td>
                    <Td isNumeric>{s.failedQueueItems || 0}</Td>
                    <Td>
                      <Flex align="center" gap={2}>
                        <Progress
                          value={rate}
                          size="sm"
                          colorScheme={rate >= 90 ? 'green' : rate >= 70 ? 'yellow' : 'red'}
                          borderRadius="full"
                          w="60px"
                        />
                        <Text fontSize="xs">{rate}%</Text>
                      </Flex>
                    </Td>
                    <Td isNumeric>
                      {(s.fundedDeals || 0) > 0 && (
                        <Badge colorScheme="green">{s.fundedDeals}</Badge>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      </Container>
    </Box>
  );
}
